import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, ThumbsUp, ThumbsDown, Clock } from 'lucide-react';
import { PublicHeader } from '@/components/Layout/PublicHeader';
import { PublicFooter } from '@/components/Layout/PublicFooter';
import { HelpSearchBar, HelpBreadcrumb, ArticleContent } from '@/components/help';
import { helpService, HelpArticle as HelpArticleType, stripMarkdown } from '@/services/helpService';
import { Spinner } from '@/components/ui/Spinner';

// Category name mapping - matches server/http/routes/help.routes.ts
const CATEGORY_NAMES: Record<string, string> = {
  'getting-started': 'Getting Started',
  'canvas-editor': 'Canvas & Editor',
  'ai-models-generation': 'AI Models & Generation',
  'account-settings': 'Account & Settings',
  'organization-users': 'Organization & Users',
  'gallery-assets': 'Gallery & Assets',
  'tips-tricks': 'Tips & Tricks',
  'troubleshooting': 'Troubleshooting',
};

export function HelpArticle() {
  const { category: categorySlug, slug: articleSlug } = useParams<{ category: string; slug: string }>();
  const [article, setArticle] = useState<HelpArticleType | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<HelpArticleType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [feedback, setFeedback] = useState<'helpful' | 'not-helpful' | null>(null);

  useEffect(() => {
    async function loadData() {
      if (!articleSlug) return;
      try {
        const data = await helpService.getArticle(articleSlug);
        setArticle(data.article);
        setRelatedArticles(data.related_articles || []);
      } catch (err) {
        console.error('Failed to load article:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [articleSlug]);

  const handleFeedback = (type: 'helpful' | 'not-helpful') => {
    setFeedback(type);
    // TODO: Send feedback to backend
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="flex justify-center items-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="container mx-auto px-4 max-w-4xl py-16 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600">The article you're looking for doesn't exist.</p>
        </div>
        <PublicFooter />
      </div>
    );
  }

  const categoryName = article.category_name || CATEGORY_NAMES[categorySlug || ''] || categorySlug || 'Help';

  // Generate excerpt for meta description
  const articleExcerpt = article.content
    ? stripMarkdown(article.content).slice(0, 160) + (article.content.length > 160 ? '...' : '')
    : `Learn about ${article.title} in the Cascady Help Center.`;
  const pageTitle = `${stripMarkdown(article.title)} | ${categoryName} | Cascady`;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={articleExcerpt} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={articleExcerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://cascady.anacreon.ai/help/${categorySlug}/${articleSlug}`} />
      </Helmet>
      <PublicHeader />

      <div className="container mx-auto px-4 max-w-6xl py-8">
        {/* Breadcrumb */}
        <HelpBreadcrumb
          items={[
            { label: categoryName, href: `/help/${categorySlug || ''}` },
            { label: article.title },
          ]}
        />

        {/* Search Bar */}
        <div className="mb-8">
          <HelpSearchBar />
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
              {/* Article Header */}
              <header className="mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-gray-100">
                <h1 className="text-xl sm:text-3xl font-semibold text-gray-900 mb-3 sm:mb-4">
                  {article.title}
                </h1>
                {article.updated_at && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>
                      Last updated:{' '}
                      {new Date(article.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                )}
              </header>

              {/* Article Content */}
              <ArticleContent content={article.content || ''} />

              {/* Feedback Section */}
              <div className="mt-12 pt-8 border-t border-gray-100">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-gray-600 font-medium">
                    Was this article helpful?
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleFeedback('helpful')}
                      disabled={feedback !== null}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        feedback === 'helpful'
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : feedback !== null
                          ? 'opacity-50 cursor-not-allowed border-gray-200 text-gray-400'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <ThumbsUp className="w-4 h-4" />
                      Yes
                    </button>
                    <button
                      onClick={() => handleFeedback('not-helpful')}
                      disabled={feedback !== null}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        feedback === 'not-helpful'
                          ? 'bg-red-50 border-red-200 text-red-700'
                          : feedback !== null
                          ? 'opacity-50 cursor-not-allowed border-gray-200 text-gray-400'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <ThumbsDown className="w-4 h-4" />
                      No
                    </button>
                  </div>
                </div>
                {feedback && (
                  <p className="mt-4 text-sm text-gray-500">
                    Thanks for your feedback!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 sticky top-24">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Related Articles
                </h3>
                <ul className="space-y-3">
                  {relatedArticles.map((related) => (
                    <li key={related.slug}>
                      <Link
                        to={`/help/${related.category}/${related.slug}`}
                        className="flex items-start gap-2 text-sm text-gray-600 hover:text-violet-600 transition-colors group"
                      >
                        <ChevronRight className="w-4 h-4 mt-0.5 text-gray-400 group-hover:text-violet-600 flex-shrink-0" />
                        <span>{related.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contact Support CTA */}
            <div className="bg-gray-50 rounded-2xl p-6 mt-4">
              <h3 className="font-semibold text-gray-900 mb-2">Need more help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Can't find what you need? Our support team is ready to assist.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center text-sm font-medium text-violet-600 hover:underline"
              >
                Contact Support
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
